import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppId, AppThunkAPIType } from "../../../app/appTypes"
import { BlobResponse, deleteWithAuth, getBlobWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http"
import { APIError } from "../../../app/appTypes"
import { DocumentHeader } from "../documentsApi"
import { DocSectionType, DocumentTypes } from "../documentTypes"

export type RiskAnalysisMethod = "FMEA" | "ISHIKAWA" | "NULL"

export interface RiskAnalysisHeader extends DocumentHeader {
    riskAnalysisMethod: RiskAnalysisMethod | null
}

export interface RiskAnalysisContent {
    groups: RiskAnalysisGroup[]
    functions: RiskAnalysisFunction[]
}

export type RiskAnalysisDocument = RiskAnalysisHeader & RiskAnalysisContent

export interface RiskAnalysisGroup {
    id: AppId
    code: string
    name: string
    numeration: string
    guid: string
    // Present on RiskAnalysisDocumentGroupDto but previously undeclared here.
    // RA groups are document sections; these are the section fields needed to
    // register them in the document section store.
    order: number
    type: DocSectionType
    parentSectionId: AppId | null
    functions: RiskAnalysisFunction[]
    groups: RiskAnalysisGroup[]
}

export interface RiskAnalysisFunction {
    id: number
    name: string
    numeration: string
    guid: string
    criticality: string | null
    defects: RiskAnalysisDefect[]
    isTestable: boolean
    testDefinitionId: AppId | null
    currentControlMethod: string
    recommendedAction: string
    hasDQ: boolean
    hasIQ: boolean
    hasOQ: boolean
    hasPQ: boolean
    order: number
}

export interface RiskAnalysisDefect {
    id: number
    name: string
    guid: string
    value: number
    consequences: RiskAnalysisConsequence[]
    functionId: number
    generated: boolean
}

export interface RiskAnalysisConsequence {
    id: number
    name: string
    guid: string
    value: number
    reasons: RiskAnalysisReason[]
    severity: number
    generated: boolean
}

export interface RiskAnalysisReason {
    id: number
    name: string
    guid: string
    value: number
    currentControlMethod: string
    generated: boolean
    occurrenceProbability: number
    detectionProbability: number
    recommendedAction: string
    hasDQ: boolean
    hasIQ: boolean
    hasOQ: boolean
    hasPQ: boolean
}

export interface RiskAnalysisSettings {
    numberingMode: string,
    goodThreshold: string,
    warningThreshold: string,
    // 'TRUE' | 'FALSE' — stored verbatim as the TESTS_ENABLED document setting.
    testsEnabled: string,
}

export interface RiskAnalysisMitigationSchedule {
    id: number
    documentId: number
    guid: string
    reasonId: number | null
    parentId: number | null
    title: string
    description: string
    responsibleUserName: string | null
    responsibleUserNameFullName: string
    dueDate: string | null
    status: string
    isManual: boolean
}

export interface RiskAnalysisRecommendedActions {
    documentId: number
    threats: {
        critical: number
        medium: number
        low: number
    }
    recommendedActions: []
}
export interface RecommendedAction {
    reasonId: number
    recommendedAction: string
    functionName: string
    defectName: string
    consequenceName: string
    riskLevel: string
    rpn: number
}

export interface CreateRiskAnalysisPayload {
    documentType: DocumentTypes,
    creationMode: string,
    structureId: AppId | undefined,
    documentNumber: string,
    templateId: string,
    documentId: string,
    title: string,
    language: string,
    settings: RiskAnalysisSettings
    description: string
    purpose: string
    isIshikawaDiagramEnabled?: boolean

}

export interface RiskAnalysisRpnLimitLevel {
    id: number | null
    minValue: number
    maxValue: number | null
    name: string
    description: string
}

export interface RiskAnalysisRpnLimits {
    low: RiskAnalysisRpnLimitLevel | null
    medium: RiskAnalysisRpnLimitLevel | null
    high: RiskAnalysisRpnLimitLevel | null
}

export enum RiskAnalysisReviewClassificationEnum {
    NotApplicable = 0,
    ReviewNotRequired = 1,
    AtLeastOneReviewRequired = 2,
    PeriodicReviewRequired = 3,
}

export interface RiskAnalysisSummaryDto {
    id: number
    documentId: number
    sectionId: string | null
    reviewClassification: RiskAnalysisReviewClassificationEnum
    scheduledReviewDate: string | null
    justification: string
}

export interface RiskAnalysisDrawing {
    id: number
    documentId: number
    format: string
    drawingData: string
}

export const loadAllRiskAnalysisDocuments = createAsyncThunk<RiskAnalysisHeader[], { filter: string }, AppThunkAPIType>("/risk-analysis/load-list",
    async ({ filter }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/risk-analysis?${filter}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadAllRiskAnalysisDocumentsByFilters = createAsyncThunk<RiskAnalysisHeader[], string, AppThunkAPIType>("/risk-analysis/load-list",
    async (filters, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/risk-analysis${filters}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const loadRiskAnalysis = createAsyncThunk<RiskAnalysisDocument, string, AppThunkAPIType>(
    "risk-analysis/load", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/risk-analysis/${id}/`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisDocument
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadRiskAnalysisContent = createAsyncThunk<RiskAnalysisContent, string, AppThunkAPIType>(
    "risk-analysis/load-content", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/risk-analysis/${id}/content`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisContent
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const downloadRiskAnalysisReport = createAsyncThunk<BlobResponse, string, AppThunkAPIType>(
    "risk-analysis/download-report", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getBlobWithAuth({
            url: `api/risk-analysis/${id}/report`,
        }))
        const { payload } = result
        if (getBlobWithAuth.fulfilled.match(result)) {
            return payload as BlobResponse
        } else {
            return rejectWithValue((payload as APIError) ?? { kind: 'unknown' })
        }
    })

export const createRiskAnalysis = createAsyncThunk<RiskAnalysisHeader, CreateRiskAnalysisPayload, AppThunkAPIType>(
    "risk-analysis/create",
    async (createPayload: CreateRiskAnalysisPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: "api/risk-analysis/create-document",
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface CreateRiskAnalysisGroup {
    documentId: string
    code: string
    name: string
    systemTypeId: string | null
    parentGroupGuid: string | null
}
export const createRiskAnalysisGroup = createAsyncThunk<RiskAnalysisGroup, CreateRiskAnalysisGroup, AppThunkAPIType>(
    "risk-analysis/create-group",
    async (args, { rejectWithValue, dispatch }) => {
        const { documentId } = args
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${documentId}/sections/add`,
            payload: args,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisGroup
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface UpdateGroupPayload {
    docId: AppId
    id: AppId
    code: string
    name: string
}
export const updateGroup = createAsyncThunk<RiskAnalysisGroup, UpdateGroupPayload, AppThunkAPIType>(
    'risk-analysis/update-group',
    async (args, { dispatch, rejectWithValue }) => {
        const { docId, ...data } = args
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/sections/edit`,
            payload: data,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisGroup
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteGroup = createAsyncThunk<AppId, { docId: AppId, groupId: AppId }, AppThunkAPIType>('risk-analysis/delete-group', async (args, api) => {
    const { docId, groupId } = args
    const { dispatch, rejectWithValue } = api
    const result = await dispatch(deleteWithAuth({
        url: `api/risk-analysis/${docId}/sections/${groupId}`,
    }))
    if (deleteWithAuth.fulfilled.match(result)) {
        return groupId
    } else {
        return rejectWithValue(result.payload ?? { kind: 'unknown' })
    }
})

export interface CreateFunction {
    documentId: AppId
    name: string
    criticality: string
    isTestable: boolean
    currentControlMethod: string
    testDefinitionId: number | null
    parentSectionGuid: AppId | null
}
export const createFunction = createAsyncThunk<RiskAnalysisFunction, CreateFunction, AppThunkAPIType>(
    "risk-analysis/create-function",
    async (args, { rejectWithValue, dispatch }) => {
        const { documentId, ...data } = args
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${documentId}/content/function`,
            payload: data,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisFunction
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const generateFunctionsAI = createAsyncThunk<unknown, AppId, AppThunkAPIType>(
    'risk-analysis/generate-functions-ai',
    async (docId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/ai/generate-functions`,
            payload: {},
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    },
)

export type UpdateFunctionPayload = Omit<RiskAnalysisFunction, 'defects'> & { docId: AppId }

export const updateFunction = createAsyncThunk<RiskAnalysisFunction, UpdateFunctionPayload, AppThunkAPIType>(
    'risk-analysis/update-function',
    async (args, { dispatch, rejectWithValue }) => {
        const { docId, id, ...data } = args
        const result = await dispatch(putWithAuth({
            url: `api/risk-analysis/${docId}/content/function/${id}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisFunction
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteFunction = createAsyncThunk<number, { docId: AppId, functionId: number }, AppThunkAPIType>('risk-analysis/delete-function', async (args, api) => {
    const { docId, functionId } = args
    const { dispatch, rejectWithValue } = api
    const result = await dispatch(deleteWithAuth({
        url: `api/risk-analysis/${docId}/function/${functionId}`,
    }))
    if (deleteWithAuth.fulfilled.match(result)) {
        return functionId
    } else {
        return rejectWithValue(result.payload ?? { kind: 'unknown' })
    }
})

// TODO(backend): the reorder route below is not implemented server-side yet.
// Front-end applies the new order optimistically; this call will fail until the
// endpoint exists. Confirm final URL/payload shape with backend before release.
export const reorderFunctions = createAsyncThunk<RiskAnalysisFunction[], { docId: AppId, orderedIds: number[] }, AppThunkAPIType>(
    'risk-analysis/reorder-functions',
    async (args, { dispatch, rejectWithValue }) => {
        const { docId, orderedIds } = args
        const result = await dispatch(putWithAuth({
            url: `api/risk-analysis/${docId}/content/functions/reorder`,
            payload: { orderedIds },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisFunction[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface CreateDefectPayload {
    docId: AppId
    name: string
    functionId: number
    value: number
}
export const createDefect = createAsyncThunk<RiskAnalysisDefect, CreateDefectPayload, AppThunkAPIType>(
    "risk-analysis/create-defect",
    async (createPayload: CreateDefectPayload, { rejectWithValue, dispatch }) => {
        const { docId, ...reqPayload } = createPayload
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/content/defects`,
            payload: reqPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisDefect
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const genDefectAI = createAsyncThunk<RiskAnalysisDefect[], { docId: AppId, fnId: number }, AppThunkAPIType>(
    'risk-analysis/gen-defect-ai',
    async (args, { dispatch, rejectWithValue }) => {
        const { fnId, docId } = args
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/functions/${fnId}/defects/generate`,
            payload: {},
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisDefect[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    },
)

export interface UpdateDefectPayload {
    docId: AppId
    id: number
    functionId: number
    name: string
    value: number
}
export const updateDefect = createAsyncThunk<RiskAnalysisDefect, UpdateDefectPayload, AppThunkAPIType>(
    'risk-analysis/update-defect',
    async (args, { dispatch, rejectWithValue }) => {
        const { docId, ...data } = args
        const result = await dispatch(putWithAuth({
            url: `api/risk-analysis/${docId}/content/defects/${data.id}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisDefect
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteDefect = createAsyncThunk<number, { docId: AppId, defectId: number }, AppThunkAPIType>('risk-analysis/delete-defect', async (args, api) => {
    const { docId, defectId } = args
    const { dispatch, rejectWithValue } = api
    const result = await dispatch(deleteWithAuth({
        url: `api/risk-analysis/${docId}/content/defects/${defectId}`,
    }))
    if (deleteWithAuth.fulfilled.match(result)) {
        return defectId
    } else {
        return rejectWithValue(result.payload ?? { kind: 'unknown' })
    }
})

export interface CreateReasonPayload {
    docId: AppId
    name: string
    consequenceId: number
    value: number
    currentControlMethod: string
}
export const createReason = createAsyncThunk<RiskAnalysisReason, CreateReasonPayload, AppThunkAPIType>(
    "risk-analysis/create-reason",
    async (args: CreateReasonPayload, { rejectWithValue, dispatch }) => {
        const { docId, ...reqPayload } = args
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/content/reasons`,
            payload: reqPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisReason
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const genReasonAI = createAsyncThunk<RiskAnalysisReason[], { docId: AppId, consequenceId: number }, AppThunkAPIType>(
    'risk-analysis/gen-reason-ai',
    async ({ docId, consequenceId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/consequences/${consequenceId}/reasons/generate`,
            payload: {},
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisReason[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    },
)

export interface UpdateReasonPayload {
    docId: AppId
    id: number
    name: string
    consequenceId: number
    currentControlMethod: string
    occurrenceProbability: number
    detectionProbability: number
    recommendedAction: string
    hasDQ: boolean
    hasIQ: boolean
    hasOQ: boolean
    hasPQ: boolean
}
export const updateReason = createAsyncThunk<RiskAnalysisReason, UpdateReasonPayload, AppThunkAPIType>(
    'risk-analysis/update-reason',
    async (args, { dispatch, rejectWithValue }) => {
        const { docId, id, ...data } = args
        const result = await dispatch(putWithAuth({
            url: `api/risk-analysis/${docId}/content/reasons/${id}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisReason
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteReason = createAsyncThunk<number, { docId: AppId, id: number }, AppThunkAPIType>('risk-analysis/delete-reason', async (args, api) => {
    const { dispatch, rejectWithValue } = api
    const { id, docId } = args
    const result = await dispatch(deleteWithAuth({
        url: `api/risk-analysis/${docId}/content/reasons/${id}`,
    }))
    if (deleteWithAuth.fulfilled.match(result)) {
        return id
    } else {
        return rejectWithValue(result.payload ?? { kind: 'unknown' })
    }
})

export interface CreateConsequencePayload {
    docId: AppId
    name: string
    defectId: number
    value: number
}

export const createConsequence = createAsyncThunk<RiskAnalysisConsequence, CreateConsequencePayload, AppThunkAPIType>(
    "risk-analysis/create-consequence",
    async (args: CreateConsequencePayload, { rejectWithValue, dispatch }) => {
        const { docId, ...data } = args
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/content/consequences`,
            payload: data,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisConsequence
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const genConsequenceAI = createAsyncThunk<RiskAnalysisConsequence[], { docId: AppId, defectId: number }, AppThunkAPIType>(
    'risk-analysis/gen-consequence-ai',
    async ({ docId, defectId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/risk-analysis/${docId}/defects/${defectId}/consequences/generate`,
            payload: {},
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisConsequence[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    },
)

export interface UpdateConsequencePayload {
    docId: AppId
    id: number
    name: string
    defectId: number
    severity: number
}
export const updateConsequence = createAsyncThunk<RiskAnalysisConsequence, UpdateConsequencePayload, AppThunkAPIType>(
    'risk-analysis/update-consequence',
    async (args, { dispatch, rejectWithValue }) => {
        const { docId, id, ...data } = args
        const result = await dispatch(putWithAuth({
            url: `api/risk-analysis/${docId}/content/consequences/${id}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisConsequence
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteConsequence = createAsyncThunk<number, { docId: AppId, id: number }, AppThunkAPIType>('risk-analysis/delete-consequence', async (args, api) => {
    const { docId, id } = args
    const { dispatch, rejectWithValue } = api
    const result = await dispatch(deleteWithAuth({
        url: `api/risk-analysis/${docId}/content/consequences/${id}`,
    }))
    if (deleteWithAuth.fulfilled.match(result)) {
        return id
    } else {
        return rejectWithValue(result.payload ?? { kind: 'unknown' })
    }
})

export const loadRiskAnalysisMitigationSchedules
    = createAsyncThunk<RiskAnalysisMitigationSchedule[], { docId: number }, AppThunkAPIType>("/risk-analysis/load-mitigation-schedules",
        async ({ docId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({ url: `api/risk-analysis/${docId}/mitigation-schedules` }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisMitigationSchedule[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export interface CreateMitigationSchedulePayload {
    documentId: number
    reasonId: number | null
    parentId: number | null
    title: string
    description: string
    responsibleUserName: string | null
    responsibleUserNameFullName: string
    dueDate: string | null
}
export const createRiskAnalysisMitigationSchedule
    = createAsyncThunk<RiskAnalysisMitigationSchedule, CreateMitigationSchedulePayload, AppThunkAPIType>(
        "risk-analysis/create-mitigation-schedule",
        async (args, { rejectWithValue, dispatch }) => {
            const { documentId } = args
            const result = await dispatch(postWithAuth({
                url: `api/risk-analysis/${documentId}/mitigation-schedules`,
                payload: args,
            }))
            const { payload } = result

            if (postWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisMitigationSchedule
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        }
    )
export const generateRiskAnalysisMitigationSchedules
    = createAsyncThunk<RiskAnalysisMitigationSchedule[], { documentId: number, clearExisting: boolean }, AppThunkAPIType>(
        "risk-analysis/generate-mitigation-schedule",
        async (args, { rejectWithValue, dispatch }) => {
            const { documentId, clearExisting } = args
            const result = await dispatch(postWithAuth({
                url: `api/risk-analysis/${documentId}/mitigation-schedules/generate?clearExisting=${clearExisting ? 'true' : 'false'}`,
                payload: {},
            }))
            const { payload } = result

            if (postWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisMitigationSchedule[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        }
    )

export interface UpdateMitigationSchedulePayload {
    id: number
    documentId: number
    title: string
    description: string
    responsibleUserName: string | null
    responsibleUserNameFullName: string
    dueDate: string | null
}
export const updateRiskAnalysisMitigationSchedule
    = createAsyncThunk<RiskAnalysisMitigationSchedule, UpdateMitigationSchedulePayload, AppThunkAPIType>(
        'risk-analysis/update-mitigation-schedule',
        async (args, { dispatch, rejectWithValue }) => {
            const { id, documentId, ...data } = args
            const result = await dispatch(putWithAuth({
                url: `api/risk-analysis/${documentId}/mitigation-schedules/${id}`,
                payload: data,
            }))
            const { payload } = result
            if (putWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisMitigationSchedule
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        }
    )

export const deleteRiskAnalysisMitigationSchedule
    = createAsyncThunk<number, { docId: AppId, id: number }, AppThunkAPIType>(
        'risk-analysis/delete-mitigation-schedule', async (args, api) => {
            const { docId, id } = args
            const { dispatch, rejectWithValue } = api
            const result = await dispatch(deleteWithAuth({
                url: `api/risk-analysis/${docId}/mitigation-schedules/${id}`,
            }))
            if (deleteWithAuth.fulfilled.match(result)) {
                return id
            } else {
                return rejectWithValue(result.payload ?? { kind: 'unknown' })
            }
        })

export const loadRiskAnalysisRecommendedActions
    = createAsyncThunk<RiskAnalysisRecommendedActions, { docId: number }, AppThunkAPIType>("/risk-analysis/load-recommended-actions",
        async ({ docId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({ url: `api/risk-analysis/${docId}/recommended-actions-summary` }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisRecommendedActions
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export const loadRiskAnalysisRpnLimits
    = createAsyncThunk<RiskAnalysisRpnLimits, { docId: number }, AppThunkAPIType>("/risk-analysis/load-rpn-limits",
        async ({ docId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({ url: `api/risk-analysis/${docId}/rpn-limits` }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisRpnLimits
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })
export const loadRiskAnalysisReviewSummary
    = createAsyncThunk<RiskAnalysisSummaryDto[], { documentId: number }, AppThunkAPIType>("/risk-analysis/load-review-summary",
        async ({ documentId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({ url: `api/risk-analysis/${documentId}/review-summary` }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisSummaryDto[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })
export const upsertRiskAnalysisReviewSummary
    = createAsyncThunk<RiskAnalysisSummaryDto, { documentId: number, summary: RiskAnalysisSummaryDto }, AppThunkAPIType>(
        'risk-analysis/upsert-review-summary',
        async (args, { dispatch, rejectWithValue }) => {
            const { documentId, summary } = args
            const result = await dispatch(postWithAuth({
                url: `api/risk-analysis/${documentId}/review-summary`,
                payload: summary,
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisSummaryDto
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        }
    )
export const updateRiskAnalysisRpnLimits
    = createAsyncThunk<RiskAnalysisRpnLimits, { documentId: number, limits: RiskAnalysisRpnLimits }, AppThunkAPIType>(
        'risk-analysis/update-rpn-limits',
        async (args, { dispatch, rejectWithValue }) => {
            const { documentId, limits } = args
            const result = await dispatch(putWithAuth({
                url: `api/risk-analysis/${documentId}/rpn-limits`,
                payload: limits,
            }))
            const { payload } = result
            if (putWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisRpnLimits
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        }
    )

export const loadRiskAnalysisDrawing = createAsyncThunk<RiskAnalysisDrawing, number, AppThunkAPIType>(
    "risk-analysis/load-drawing", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/risk-analysis/${id}/ishikawa-diagram`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisDrawing
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const upsertRiskAnalysisDrawing = createAsyncThunk<RiskAnalysisDrawing, { format: string, drawingData: string, docId: number }, AppThunkAPIType>(
    "risk-analysis/upsert-drawing",
    async (args, { rejectWithValue, dispatch }) => {
        const { docId, ...data } = args
        const result = await dispatch(putWithAuth({
            url: `api/risk-analysis/${docId}/ishikawa-diagram`,
            payload: data,
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as RiskAnalysisDrawing
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)
export const reorderRiskAnalysisFunctions
    = createAsyncThunk<RiskAnalysisDocument, { documentId: string, functions: { id: number }[] }, AppThunkAPIType>(
        "risk-analysis/reorder-functions",
        async (args, { rejectWithValue, dispatch }) => {
            const { documentId, ...data } = args
            const result = await dispatch(postWithAuth({
                url: `api/risk-analysis/${documentId}/content/functions/reorder`,
                payload: data,
            }))
            const { payload } = result

            if (postWithAuth.fulfilled.match(result)) {
                return payload as RiskAnalysisDocument
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        }
    )

export function* allRAFunctions(ra: RiskAnalysisContent) {
    for (let i = 0; i < ra.functions.length; i++) {
        const fn = ra.functions[i];
        yield fn
    }
    for (const type of ra.groups) {
        for (const fn of type.functions) {
            yield fn
        }
        for (const group of type.groups) {
            for (const innerFn of group.functions) {
                yield innerFn
            }
        }
    }
}

export const defRAFunction: RiskAnalysisFunction = {
    id: 0,
    name: '',
    numeration: '',
    guid: '',
    criticality: null,
    defects: [],
    isTestable: false,
    testDefinitionId: null,
    currentControlMethod: '',
    recommendedAction: '',
    hasDQ: false,
    hasIQ: false,
    hasOQ: false,
    hasPQ: false,
    order: 0,
}
